import { zodResolver } from '@hookform/resolvers/zod';
import { CircleAlert } from 'lucide-react';
import { useMemo, useState } from 'react';
import { Controller, useForm, useWatch } from 'react-hook-form';
import { z } from 'zod';

import { useCategoriesStore } from '@/features/categories/store';
import { useActiveGroup } from '@/features/groups/store';
import { useLedgerStore } from '@/features/ledger/store';
import { cn } from '@/lib/cn';
import { todayISO } from '@/lib/date';
import {
  Avatar,
  Button,
  Checkbox,
  Input,
  Modal,
  Money,
  SegmentedControl,
  Select,
  Textarea,
  toast,
} from '@/ui';
import {
  amountInputValue,
  currencySymbol,
  formatMoney,
  parseAmountInput,
  sum,
} from '@domain/money';
import {
  computeSplit,
  defaultValuesForSplitType,
  validateSplit,
  type ParticipantInput,
} from '@domain/split';
import {
  SPLIT_TYPES,
  SPLIT_TYPE_HINTS,
  SPLIT_TYPE_LABELS,
  type SplitType,
} from '@domain/types';

import { useExpenseEditor } from './editor';

const expenseSchema = z.object({
  title: z
    .string()
    .trim()
    .min(1, 'Give the expense a title.')
    .max(80, 'Keep it under 80 characters.'),
  amount: z.string().refine((value) => {
    const minor = parseAmountInput(value);
    return minor !== null && minor > 0;
  }, 'Enter an amount greater than zero.'),
  categoryId: z.string(),
  paidBy: z.string().min(1, 'Choose who paid.'),
  date: z.string().min(1, 'Pick a date.'),
  notes: z.string().max(500, 'Keep notes under 500 characters.'),
});

type ExpenseFormValues = z.infer<typeof expenseSchema>;

/**
 * Starting values for the split column after the split's shape changes.
 * `equal` and `custom` need no input: equal derives everything, and a blank
 * cell in `custom` means "give me an even cut of whatever is left".
 */
function seedSplitValues(
  splitType: SplitType,
  totalMinor: number,
  memberIds: string[]
): Record<string, string> {
  if (splitType === 'equal' || splitType === 'custom') return {};

  return Object.fromEntries(
    defaultValuesForSplitType(splitType, totalMinor, memberIds).map((entry) => [
      entry.memberId,
      entry.value === null
        ? ''
        : splitType === 'exact'
          ? amountInputValue(entry.value)
          : String(entry.value),
    ])
  );
}

export function ExpenseEditorModal() {
  const open = useExpenseEditor((state) => state.open);
  const expenseId = useExpenseEditor((state) => state.expenseId);
  const close = useExpenseEditor((state) => state.close);

  // Remounting per target resets every field, including the split grid.
  return open ? <EditorBody key={expenseId ?? 'new'} onClose={close} expenseId={expenseId} /> : null;
}

function EditorBody({
  expenseId,
  onClose,
}: {
  expenseId: string | null;
  onClose: () => void;
}) {
  const group = useActiveGroup();
  const members = useLedgerStore((state) => state.members);
  const addExpense = useLedgerStore((state) => state.addExpense);
  const editExpense = useLedgerStore((state) => state.editExpense);
  const categories = useCategoriesStore((state) => state.categories);

  const expense = useLedgerStore((state) =>
    expenseId ? (state.allExpenses.find((candidate) => candidate.id === expenseId) ?? null) : null
  );

  const currency = group?.currency ?? 'USD';

  /** Active members, plus any inactive ones already on this expense. */
  const selectableMembers = useMemo(() => {
    const attached = new Set([
      ...(expense?.participants.map((participant) => participant.memberId) ?? []),
      ...(expense ? [expense.paidBy] : []),
    ]);
    return members.filter((member) => member.isActive || attached.has(member.id));
  }, [members, expense]);

  const {
    control,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<ExpenseFormValues>({
    resolver: zodResolver(expenseSchema),
    defaultValues: {
      title: expense?.title ?? '',
      amount: expense ? amountInputValue(expense.amountMinor) : '',
      categoryId: expense?.categoryId ?? '',
      paidBy: expense?.paidBy ?? selectableMembers[0]?.id ?? '',
      date: expense?.date ?? todayISO(),
      notes: expense?.notes ?? '',
    },
  });

  // `useWatch` rather than `watch()`: it subscribes through the control, which
  // stays stable across renders and keeps the split preview in sync.
  const amountText = useWatch({ control, name: 'amount' });
  const totalMinor = parseAmountInput(amountText) ?? 0;

  const [splitType, setSplitType] = useState<SplitType>(expense?.splitType ?? 'equal');

  const [selectedIds, setSelectedIds] = useState<string[]>(() =>
    expense
      ? expense.participants.map((participant) => participant.memberId)
      : selectableMembers.map((member) => member.id)
  );

  /** Raw text per member; what it means depends on `splitType`. */
  const [splitValues, setSplitValues] = useState<Record<string, string>>(() => {
    if (!expense) return {};
    return Object.fromEntries(
      expense.participants.map((participant) => [
        participant.memberId,
        expense.splitType === 'percentage' || expense.splitType === 'shares'
          ? String(participant.weight ?? '')
          : amountInputValue(participant.shareAmountMinor),
      ])
    );
  });

  /*
   * Re-seed the split column when the shape of the split changes — a different
   * method, or a participant added or removed. Adjusting state during render
   * (rather than in an effect) is React's documented pattern for "reset some
   * state when a prop/derived value changes": no cascading second render, and
   * it can't fire while the user is mid-keystroke in the amount field, since
   * the amount isn't part of the signature.
   */
  const signature = `${splitType}:${selectedIds.join(',')}`;
  const [lastSignature, setLastSignature] = useState(signature);

  if (lastSignature !== signature) {
    setLastSignature(signature);
    setSplitValues(seedSplitValues(splitType, totalMinor, selectedIds));
  }

  const participantInputs: ParticipantInput[] = useMemo(
    () =>
      selectedIds.map((memberId) => {
        const raw = splitValues[memberId] ?? '';
        if (splitType === 'equal' || raw.trim() === '') return { memberId, value: null };

        if (splitType === 'exact' || splitType === 'custom') {
          return { memberId, value: parseAmountInput(raw) };
        }

        const numeric = Number(raw.replace(/[^\d.-]/g, ''));
        return { memberId, value: Number.isFinite(numeric) ? numeric : null };
      }),
    [selectedIds, splitValues, splitType]
  );

  const shares = useMemo(
    () => computeSplit(splitType, totalMinor, participantInputs),
    [splitType, totalMinor, participantInputs]
  );

  const splitError = useMemo(
    () =>
      validateSplit(splitType, totalMinor, participantInputs, (minor) =>
        formatMoney(minor, currency)
      ),
    [splitType, totalMinor, participantInputs, currency]
  );

  const shareByMember = useMemo(
    () => new Map(shares.map((share) => [share.memberId, share.shareAmountMinor])),
    [shares]
  );

  const allocatedMinor = sum(shares.map((share) => share.shareAmountMinor));
  const balanced = allocatedMinor === totalMinor && totalMinor > 0;

  function toggleMember(memberId: string, checked: boolean) {
    setSelectedIds((current) =>
      checked ? [...current, memberId] : current.filter((id) => id !== memberId)
    );
  }

  async function submit(values: ExpenseFormValues) {
    if (splitError) return;

    const payload = {
      title: values.title,
      categoryId: values.categoryId || null,
      amountMinor: totalMinor,
      paidBy: values.paidBy,
      date: values.date,
      notes: values.notes,
      splitType,
      shares,
    };

    try {
      if (expenseId) {
        await editExpense(expenseId, payload);
        toast.success('Expense updated.');
      } else {
        await addExpense(payload);
        toast.success('Expense added.');
      }
      onClose();
    } catch (error) {
      toast.error((error as Error).message || 'Could not save the expense.');
    }
  }

  const symbol = currencySymbol(currency);

  if (!group || selectableMembers.length === 0) {
    return (
      <Modal
        open
        onClose={onClose}
        title={expenseId ? 'Edit expense' : 'Add expense'}
        width="sm"
        footer={
          <Button variant="secondary" onClick={onClose}>
            Close
          </Button>
        }
      >
        <p className="text-sm text-muted">
          {!group
            ? 'Create or select a group before recording expenses.'
            : 'This group has no members yet — an expense needs someone to pay it and someone to share it.'}
        </p>
      </Modal>
    );
  }

  return (
    <Modal
      open
      onClose={onClose}
      title={expenseId ? 'Edit expense' : 'Add expense'}
      description={group.name}
      width="xl"
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button
            loading={isSubmitting}
            disabled={Boolean(splitError)}
            onClick={handleSubmit(submit)}
          >
            {expenseId ? 'Save changes' : 'Add expense'}
          </Button>
        </>
      }
    >
      <div className="grid grid-cols-1 gap-6 md:grid-cols-[minmax(0,1fr)_minmax(0,1.15fr)]">
        <div className="flex flex-col gap-4">
          <Controller
            control={control}
            name="amount"
            render={({ field }) => (
              <Input
                label="Amount"
                placeholder="0.00"
                prefix={symbol}
                inputMode="decimal"
                autoComplete="off"
                className="text-lg font-semibold"
                error={errors.amount?.message}
                {...field}
              />
            )}
          />

          <Controller
            control={control}
            name="title"
            render={({ field }) => (
              <Input
                label="What was it for?"
                placeholder="Dinner at Nando's"
                autoComplete="off"
                error={errors.title?.message}
                {...field}
              />
            )}
          />

          <div className="grid gap-4 sm:grid-cols-2">
            <Controller
              control={control}
              name="paidBy"
              render={({ field }) => (
                <Select
                  label="Paid by"
                  error={errors.paidBy?.message}
                  options={selectableMembers.map((member) => ({
                    value: member.id,
                    label: member.isActive ? member.name : `${member.name} (inactive)`,
                  }))}
                  {...field}
                />
              )}
            />

            <Controller
              control={control}
              name="date"
              render={({ field }) => (
                <Input label="Date" type="date" error={errors.date?.message} {...field} />
              )}
            />
          </div>

          <Controller
            control={control}
            name="categoryId"
            render={({ field }) => (
              <Select
                label="Category"
                placeholder="Uncategorised"
                options={categories.map((category) => ({
                  value: category.id,
                  label: category.name,
                }))}
                {...field}
              />
            )}
          />

          <Controller
            control={control}
            name="notes"
            render={({ field }) => (
              <Textarea
                label="Notes"
                placeholder="Anything worth remembering (optional)"
                error={errors.notes?.message}
                {...field}
              />
            )}
          />
        </div>

        <div className="flex flex-col gap-3">
          <div>
            <span className="text-[13px] font-medium text-muted">Split</span>
            <div className="mt-1.5 overflow-x-auto scroll-thin">
              <SegmentedControl
                size="sm"
                value={splitType}
                onChange={setSplitType}
                segments={SPLIT_TYPES.map((type) => ({
                  value: type,
                  label: SPLIT_TYPE_LABELS[type],
                }))}
              />
            </div>
            <p className="mt-1.5 text-[12px] text-muted">{SPLIT_TYPE_HINTS[splitType]}</p>
          </div>

          <div className="overflow-hidden rounded-xl border border-line">
            <div className="max-h-72 overflow-y-auto scroll-thin">
              {selectableMembers.map((member) => {
                const selected = selectedIds.includes(member.id);
                const share = shareByMember.get(member.id) ?? 0;

                return (
                  <div
                    key={member.id}
                    className={cn(
                      'flex items-center gap-3 border-b border-line px-3 py-2 last:border-b-0',
                      !selected && 'opacity-55'
                    )}
                  >
                    <Checkbox
                      checked={selected}
                      onChange={(checked) => toggleMember(member.id, checked)}
                      label={
                        <span className="flex items-center gap-2">
                          <Avatar name={member.name} id={member.id} size="xs" />
                          <span className="truncate">{member.name}</span>
                        </span>
                      }
                    />

                    <span className="ml-auto flex items-center gap-2">
                      {selected && splitType !== 'equal' && (
                        <Input
                          aria-label={`${SPLIT_TYPE_LABELS[splitType]} value for ${member.name}`}
                          value={splitValues[member.id] ?? ''}
                          onChange={(event) =>
                            setSplitValues((current) => ({
                              ...current,
                              [member.id]: event.target.value,
                            }))
                          }
                          placeholder={splitType === 'custom' ? 'auto' : '0'}
                          prefix={
                            splitType === 'exact' || splitType === 'custom' ? symbol : undefined
                          }
                          suffix={splitType === 'percentage' ? '%' : undefined}
                          inputMode="decimal"
                          containerClassName="w-32"
                          className="text-right"
                        />
                      )}

                      <span className="w-24 text-right text-[13px]">
                        {selected ? (
                          <Money minor={share} currency={currency} />
                        ) : (
                          <span className="text-muted">—</span>
                        )}
                      </span>
                    </span>
                  </div>
                );
              })}
            </div>

            <div className="flex items-center justify-between gap-3 border-t border-line bg-elevated/50 px-3 py-2.5 text-[13px]">
              <span className="text-muted">
                {selectedIds.length} participant{selectedIds.length === 1 ? '' : 's'}
              </span>
              <span className={cn('tnum font-semibold', balanced ? 'text-success' : 'text-muted')}>
                {formatMoney(allocatedMinor, currency)} of {formatMoney(totalMinor, currency)}
              </span>
            </div>
          </div>

          {splitError && (
            <p className="flex items-start gap-2 rounded-xl bg-danger/10 px-3 py-2.5 text-[12px] text-danger">
              <CircleAlert size={14} className="mt-px shrink-0" />
              {splitError}
            </p>
          )}
        </div>
      </div>
    </Modal>
  );
}
